import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import axios from "axios";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./Patient.css";
import PatientDetailPopup from "./Popup/PatientDetailPopup";
import { formatDate } from "../../../components/Util/DateFormat";
import * as PatientFunctions from "../../../functions/Functions";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../utils/portConfig";

function Patient() {
  const [patientData, setPatientData] = useState([]); // 'orders' 변수를 useState로 정의
  const [showPatientPopup, setshowPatientPopup] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [popupData, setPopupData] = useState(null);

  // 테이블 높이 설정값
  const [pageSize, setPageSize] = useState(10); // 기본값 10으로 시작
  const gridContainerRef = useRef(null);

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1`;
  const token = localStorage.getItem("token");
  const id = localStorage.getItem("id");

  const gridRef = useRef(null);

  // Define onGridReady function if needed
  const onGridReady = (params) => {
    // You can handle grid ready logic here if necessary
  };

  // 화면이 그려질 때 사용자 데이터 조회
  useEffect(() => {
    fetchData(); // 초기 로드에서 검색 매개 변수 없이 호출
  }, []);

  const fetchData = async () => {
    // 1. 로딩 알림 표시 시작
    Swal.fire({
      title: "데이터를 가져오는 중...",
      text: "잠시만 기다려 주세요.",
      didOpen: () => {
        Swal.showLoading();
      },
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    try {
      const response = await axios.get(`${API_SERVER_URL}/checklist/getPatientList/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const replaceData = response.data.map((item) => ({
        age: item.age,
        birthDueDate: item.birthDueDate ? item.birthDueDate.replace(/(\d{4})(\d{2})(\d{2})/, "$1.$2.$3") : "",
        doctorName: item.doctorName,
        //hospitalId: item.hospitalid,
        hospitalName: item.hospitalName,
        lastVisitDate: item.lastVisitDate,
        patientId: item.patientId,
        patientName: item.patientName,
        pregWeek: item.pregWeek,
      }));

      setPatientData(replaceData);
    } catch (error) {
      console.error("Data fetch error:", error);
      Swal.fire("오류!", "데이터를 가져오는 중 오류가 발생했습니다.", "error");
    } finally {
      // 2. 로딩 알림 닫기
      Swal.close();
    }
  };

  // 그리드 컬럼 작성
  const columns = [
    {
      field: "patientName",
      cellStyle: { textAlign: "left" },
      headerName: "환자명",
      flex: 1,
    },
    {
      field: "age",
      headerName: "나이",
      flex: 1,
    },
    {
      field: "pregWeek",
      headerName: "임신 주 수",
      flex: 1,
    },
    {
      field: "birthDueDate",
      headerName: "출산예정일",
      flex: 1,
    },
    {
      field: "hospitalName",
      cellStyle: { textAlign: "left" },
      headerName: "병원명",
      width: 300,
    },
    {
      field: "doctorName",
      headerName: "주치의",
      flex: 1,
    },
    {
      field: "lastVisitDate",
      headerName: "마지막 방문일",
      valueFormatter: (params) => formatDate(params.data.lastVisitDate),
      flex: 1,
    },
    {
      field: "patientId",
      hide: true, // 숨겨진 필드로 설정(환자ID)
    },
  ];

  // 그리드 옵션 추가
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
    };
  }, []);

  const onSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    if (selectedNodes.length > 0) {
      const selectedIndex = selectedNodes[0].rowIndex;
      setSelectedRowIndex(selectedIndex);
    }
    setshowPatientPopup(true);
  };

  // 팝업 닫기 함수
  const closePopup = () => {
    setshowPatientPopup(false);
    setSelectedRowIndex(null);
    // if(gridRef.current) {
    //   gridRef.current.api.deselectAll();
    //   setSelectedPatient(null);
    //   setshowPatientPopup(false);
    // }
  };

  // 테이블 높이 변경 함수
  const calculatePageSize = () => {
    if (gridContainerRef.current) {
      const gridHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 43; // AG Grid의 기본 행 높이 (alpine 테마)
      const headerHeight = 45; // 헤더 높이
      const paginationHeight = 45; // 페이지네이션 아래 여백

      const availableRows = Math.floor((gridHeight - headerHeight - paginationHeight) / rowHeight);

      // 최소 5개, 계산된 값보다 작은 행 수로 설정
      const newPageSize = Math.max(5, availableRows);
      setPageSize(newPageSize);
    }
  };

  // 컴포넌트 마운트 및 창 크기 변경 시 테이블 높이 조정
  useEffect(() => {
    calculatePageSize();

    // 창 크기 변경 이벤트 리스너 추가
    window.addEventListener("resize", calculatePageSize);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("resize", calculatePageSize);
    };
  }, []);

  // 데이터 로드 후 그리드 크기에 맞게 테이블 높이 재계산
  useEffect(() => {
    if (patientData.length > 0) {
      setTimeout(calculatePageSize, 100); // 데이터 로드 후 약간의 지연을 두고 계산
    }
  }, [patientData]);

  return (
    <div className="dashboard-content">
      <div className="dashboard-content-container">
        <div className="dashboard-content-header">
          <h2>환자 정보 조회</h2>
        </div>

        <PatientDetailPopup isOpen={showPatientPopup} onClose={closePopup} patientDetails={patientData[selectedRowIndex]} />

        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: "100%", width: "100%", fontWeight: "400" }}>
          <AgGridReact
            ref={(grid) => (gridRef.current = grid)}
            onGridReady={onGridReady}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={pageSize}
            defaultColDef={defaultColDef}
            rowData={patientData} // Set the data source for Ag-Grid
            onRowClicked={onSelectionChanged}
            rowSelection="single" // Set row selection to single
            domLayout="autoHeight"
            multiSortKey="shift"
          />
        </div>
      </div>
    </div>
  );
}

export default Patient;
